import React, { useState, useEffect } from "react";
import {
	Paper,
	Button,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Table,
	TableBody,
	TableCell,
	TableRow,
	TableHead,
	Alert,
	Typography,
	Box,
} from "@mui/material";
import axiosInstance from "../utills/axiosInstance";

const PayrollPackage = () => {
	const [employee, setEmployee] = useState(null);
	const [employees, setEmployees] = useState([]);
	const [ctc, setCtc] = useState("");
	const [components, setComponents] = useState([]);
	const [selectedComponents, setSelectedComponents] = useState([]);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [monthlyCalCTC, setMonthlyCalCTC] = useState(0);
	const [monthlyGrossPay, setMonthlyGrossPay] = useState(0);
	const [monthlyTakeHome, setMonthlyTakeHome] = useState(0);

	const [bankDetails, setBankDetails] = useState({
		accountNumber: "",
		ifsc: "",
		bankName: "",
		branch: ""
	});

	useEffect(() => {
		fetchEmployees();
		fetchComponents();
	}, []);

	const fetchEmployees = async () => {
		try {
			const res = await axiosInstance.get("/api/images");
			setEmployees(res.data || []);
		} catch (err) {
			setError("Failed to load employees");
		}
	};

	const fetchComponents = async () => {
		try {
			const res = await axiosInstance.get("/api/payroll/get-components");
			setComponents(res?.data?.data || []);
		} catch (err) {
			setError("Failed to load components");
		}
	};

	const fetchEmployeePackage = async (employeeId) => {
		try {
			const res = await axiosInstance.get(`/api/payroll/package/${employeeId}`);
			setCtc(res.data.ctc || "");
			setEmployee(employees.find(e => e._id === employeeId));

			// FIXED: Load real value from DB
			const loaded = (res.data.components || []).map(c => ({
				component: c.component._id,
				value: c.value || 0,
			}));
			setSelectedComponents(loaded);

			setBankDetails(res.data.bankDetails || { accountNumber: "", ifsc: "", bankName: "", branch: "" });
		} catch (err) {
			setCtc("");
			setSelectedComponents([]);
			setBankDetails({ accountNumber: "", ifsc: "", bankName: "", branch: "" });
		}
	};

	const getComponent = (id) => components.find(c => c._id === id);

	const normalizeName = (name) => name.toLowerCase().replace(/\s+/g, '_');

	const calculatePay = () => {
		if (!ctc || parseFloat(ctc) <= 0) {
			setMonthlyGrossPay(0);
			setMonthlyTakeHome(0);
			setMonthlyCalCTC(0);
			return;
		}

		const monthlyCTC = parseFloat(ctc) / 12;

		let totalEarnings = 0;
		let employeeDeductions = 0;
		let employerContributions = 0;

		const calculatedAmounts = {};

		selectedComponents.forEach(sc => {
			const comp = getComponent(sc.component);
			if (!comp || !comp.isActive) return;

			let amount = 0;

			const baseForCheck = monthlyCTC;

			if (comp.max > 0 && baseForCheck > comp.max) return;
			if (comp.min > 0 && baseForCheck < comp.min) return;

			if (comp.isFixed) {
				amount = parseFloat(sc.value) || 0;
			}
			else if (comp.basedOn === "gross") {
				const percentage = parseFloat(sc.value) || 0;
				amount = (percentage / 100) * monthlyCTC;
			}

			if (comp.cap > 0) {
				amount = Math.min(amount, comp.cap);
			}

			calculatedAmounts[sc.component] = amount;

			if (comp.type === "earning") totalEarnings += amount;
			else if (comp.type === "deduction") employeeDeductions += amount;
			else if (comp.type === "statutory") employerContributions += amount;
		});

		selectedComponents.forEach(sc => {
			const comp = getComponent(sc.component);
			if (!comp || comp.isFixed || comp.basedOn === "gross") return;

			const baseComp = selectedComponents.find(s => {
				const bComp = getComponent(s.component);
				return normalizeName(bComp?.name) === normalizeName(comp.basedOn);
			});
			if (!baseComp) return;

			const baseAmount = calculatedAmounts[baseComp.component] || 0;

			if (comp.max > 0 && baseAmount > comp.max) return;
			if (comp.min > 0 && baseAmount < comp.min) return;

			let cappedBase = baseAmount;
			if (comp.cap > 0) cappedBase = Math.min(baseAmount, comp.cap);
			console.log(comp.name,cappedBase,comp.cap);

			const percentage = parseFloat(sc.value) || 0;
			const amount = (percentage / 100) * cappedBase;

			calculatedAmounts[sc.component] = amount;

			if (comp.type === "earning") totalEarnings += amount;
			else if (comp.type === "deduction") employeeDeductions += amount;
			else if (comp.type === "statutory") employerContributions += amount;
		});

		const grossPay = totalEarnings;
		const takeHome = totalEarnings - employeeDeductions;
		const calculatedCTC = totalEarnings + employerContributions;

		setMonthlyCalCTC(calculatedCTC);
		setMonthlyGrossPay(grossPay);
		setMonthlyTakeHome(takeHome);
	};

	useEffect(() => {
		calculatePay();
	}, [ctc, selectedComponents, components]);

	const handleAddComponent = (comp) => {
		if (selectedComponents.some(sc => sc.component === comp._id)) return;

		setSelectedComponents(prev => [
			...prev,
			{ component: comp._id, value: comp.value || 0 }
		]);
	};

	const handleValueChange = (index, newValue) => {
		const updated = [...selectedComponents];
		updated[index].value = newValue === "" ? "" : parseFloat(newValue) || 0;
		setSelectedComponents(updated);
	};

	const handleRemove = (index) => {
		setSelectedComponents(prev => prev.filter((_, i) => i !== index));
	};

	const handleBankChange = (field, value) => {
		setBankDetails(prev => ({
			...prev,
			[field]: value
		}));
	};

	const handleSubmit = async () => {
		try {
			await axiosInstance.post("/api/payroll/configure", {
				employeeId: employee._id,
				ctc: parseFloat(ctc),
				components: selectedComponents.map(sc => ({
					component: sc.component,
					value: parseFloat(sc.value) || 0
				})),

				bankDetails
			});
			setSuccess("Payroll package saved successfully!");
			setTimeout(() => setSuccess(""), 4000);
		} catch (err) {
			setError(err.response?.data?.message || "Failed to save package");
		}
	};

	return (
		<Paper sx={{ p: 3 }}>
			<Typography variant="h5" gutterBottom>
				Configure Payroll Package
			</Typography>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			{success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

			<FormControl fullWidth sx={{ mb: 2, mt: 3 }}>
				<InputLabel>Select Employee</InputLabel>
				<Select
					value={employee?._id || ""}
					label="Select Employee"
					onChange={(e) => {
						const id = e.target.value;
						if (!id) {
							setEmployee(null); setCtc(""); setSelectedComponents([]);
							setBankDetails({ accountNumber: "", ifsc: "", bankName: "", branch: "" });
							return;
						}
						const emp = employees.find(u => u._id === id);
						setEmployee(emp);
						fetchEmployeePackage(id);
					}}
				>
					<MenuItem value=""><em>Select Employee</em></MenuItem>
					{employees.map(emp => (
						<MenuItem key={emp._id} value={emp._id}>
							{emp.firstName} {emp.lastName}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{employee && (
				<>
					<TextField
						label="Annual CTC"
						type="number"
						fullWidth
						value={ctc}
						onChange={(e) => setCtc(e.target.value)}
						sx={{ mb: 2 }}
					/>

					{ctc && (
						<Alert severity="info" sx={{ mb: 2 }}>
							<strong style={{ color: "gray" }}>Monthly CTC:</strong> ₹{monthlyCalCTC.toFixed(0)} <br />
							<strong style={{ color: "gray" }}>Monthly Gross Pay:</strong> ₹{monthlyGrossPay.toFixed(0)} <br />
							<strong style={{ color: "gray" }}>Monthly Net Pay (Take Home):</strong> ₹{monthlyTakeHome.toFixed(0)}
						</Alert>
					)}

					<Typography variant="h6" gutterBottom>
						Available Components
					</Typography>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Value</TableCell>
								<TableCell>Add</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{components.map(c => (
								<TableRow key={c._id}>
									<TableCell>{c.name}</TableCell>
									<TableCell>{c.type}</TableCell>
									<TableCell>{c.value}{c.isFixed ? '' : '%'}</TableCell>
									<TableCell>
										<Button
											size="small"
											variant="outlined"
											onClick={() => handleAddComponent(c)}
											disabled={selectedComponents.some(sc => sc.component === c._id)}
										>
											Add
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					<Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
						Selected Components
					</Typography>

					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Component</TableCell>
								<TableCell>Edit Value</TableCell>
								<TableCell>Action</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{selectedComponents.map((sc, idx) => {
								const comp = getComponent(sc.component);
								const unit = comp?.isFixed ? "₹" : "%";

								return (
									<TableRow key={idx}>
										<TableCell>{comp?.name}</TableCell>
										<TableCell>
											<TextField
												size="small"
												type="number"
												value={sc.value}
												onChange={(e) => handleValueChange(idx, e.target.value)}
												InputProps={{ endAdornment: unit }}
												sx={{ width: 160 }}
											/>
										</TableCell>
										<TableCell>
											<Button size="small" color="error" onClick={() => handleRemove(idx)}>
												Remove
											</Button>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>

					<Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
						Bank Details
					</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<TextField label="Account Number" value={bankDetails.accountNumber} onChange={(e) => handleBankChange('accountNumber', e.target.value)} fullWidth />
						<TextField label="IFSC Code" value={bankDetails.ifsc} onChange={(e) => handleBankChange('ifsc', e.target.value)} fullWidth />
						<TextField label="Bank Name" value={bankDetails.bankName} onChange={(e) => handleBankChange('bankName', e.target.value)} fullWidth />
						<TextField label="Branch" value={bankDetails.branch} onChange={(e) => handleBankChange('branch', e.target.value)} fullWidth />
					</Box>

					<Button variant="contained" color="primary" size="large" onClick={handleSubmit} sx={{ mt: 3 }}>
						Save Package
					</Button>
				</>
			)}
		</Paper>
	);
};

export default PayrollPackage;
